import { Deviation_Chart, Deviation_Group, Deviation_State } from './deviation-chart'
import { set_deviation_config } from './deviation'

/**
 * CSS-Klasse des Containers, in den der Chart gerendert wird.
 */
const CONTAINER_SELECTOR = '.gt-deviation-chart-container'

/**
 * ID der Umschaltung für die Angebots-Spur.
 */
const OFFER_TOGGLE_ID = 'gt-deviation-chart-offer'

/**
 * Datumsangaben je Bereich, Slug als Schlüssel. Aus den Metas
 * 'project-approval-dates' und 'project-start-dates'.
 */
type Area_Dates = Record<string, unknown> | null | undefined

/**
 * Hängt die Abweichungs-Visualisierung in die Projektansicht ein.
 *
 * Passiert nichts, wenn der Container fehlt oder kein Payload vorliegt.
 *
 * @param approval_dates Freigaben aus dem Meta 'project-approval-dates'. Ein
 *                       Eintrag bedeutet, dass der Bereich abgeschlossen ist.
 * @param start_dates    Startzeitpunkte aus dem Meta 'project-start-dates'. Ein
 *                       Eintrag ohne Freigabe bedeutet, dass der Bereich läuft.
 *
 * @returns Die Chart-Instanz oder null, wenn nichts zu rendern war.
 */
export function mount_deviation_chart(
    approval_dates: Area_Dates = null,
    start_dates: Area_Dates = null,
): Deviation_Chart | null {
    const container = document.querySelector<HTMLElement>( CONTAINER_SELECTOR )

    if ( ! container ) {
        return null
    }

    if ( typeof gt_report === 'undefined' || ! gt_report ) {
        return null
    }

    set_deviation_config( gt_report.deviation )

    const project = gt_report.project

    const has_entry = ( dates: Area_Dates, slug: string ): boolean => {
        return !! dates && Object.prototype.hasOwnProperty.call( dates, slug )
    }

    // Ohne Freigabedaten lässt sich kein Zustand ableiten. Dann gilt alles als
    // abgeschlossen, damit der Chart nicht fälschlich alles als Vorschau zeigt.
    const get_state = ( slug: string ): Deviation_State => {
        if ( ! approval_dates ) {
            return 'complete'
        }

        if ( has_entry( approval_dates, slug ) ) {
            return 'complete'
        }

        return has_entry( start_dates, slug ) ? 'running' : 'pending'
    }

    const open_areas = project.statuses.filter( ( status ) => get_state( status.slug ) !== 'complete' ).length

    /**
     * Beschriftung des Badges einer Gruppe.
     *
     * Ein Badge markiert immer eine Abweichung vom Normalfall. Bei offenen
     * Bereichen übernimmt der Zustand die Beschriftung, bei abgeschlossenen
     * bleibt nur eine fehlende Prognose als Auffälligkeit.
     *
     * @param state    Zustand des Bereichs.
     * @param complete Ob beide Prognosen gepflegt sind.
     *
     * @returns Beschriftung oder undefined.
     */
    const get_state_label = ( state: Deviation_State, complete: boolean ): string | undefined => {
        if ( state === 'complete' && ! complete ) {
            return 'unvollständig'
        }

        return undefined
    }

    /**
     * Beschriftung des Badges der Gesamtzeiten.
     *
     * @returns Beschriftung oder undefined.
     */
    const get_totals_label = (): string | undefined => {
        if ( open_areas > 0 ) {
            return 'vorläufig'
        }

        if ( project.totals.missing_offer > 0 || project.totals.missing_estimate > 0 ) {
            return 'unvollständig'
        }

        return undefined
    }

    const groups: Deviation_Group[] = [
        {
            label: project.totals.label,
            delta_offer: project.totals.delta_offer,
            delta_estimate: project.totals.delta_estimate,
            offer: project.totals.offer,
            estimate: project.totals.estimate,
            net: project.totals.net,
            missing_offer: project.totals.missing_offer,
            missing_estimate: project.totals.missing_estimate,
            open_areas,

            // Nur kennzeichnen, was vom Normalfall abweicht. Ein Badge
            // "abgeschlossen" wäre reines Rauschen.
            state_label: get_totals_label(),
            state_tone: get_totals_label() === 'unvollständig' ? 'warning' : 'neutral',
            tolerance: gt_report.deviation.tolerance_total,
            divider: true,
        },
    ]

    // Die Bereiche lesen sich als eine zusammenhängende Grafik, daher nur unter
    // dem letzten eine Skalenzeile.
    project.statuses.forEach( ( status, index ) => {
        const state = get_state( status.slug )

        groups.push( {
            label: status.label,
            slug: status.slug,
            delta_offer: status.delta_offer,
            delta_estimate: status.delta_estimate,
            offer: status.offer,
            estimate: status.estimate,
            net: status.net,
            has_offer: status.has_offer,
            has_estimate: status.has_estimate,
            state,
            state_label: get_state_label( state, status.has_offer && status.has_estimate ),
            state_tone: state === 'complete' ? 'warning' : 'neutral',
            reason: status.reason,
            tolerance: gt_report.deviation.tolerance_status,
            scale: index === project.statuses.length - 1,
        } )
    } )

    const canvas = render_offer_toggle( container )
    const chart = new Deviation_Chart( canvas )

    chart.set_groups( groups )

    container.querySelector<HTMLInputElement>( `#${ OFFER_TOGGLE_ID }` )
        ?.addEventListener( 'change', ( event ) => {
            chart.set_offer( ( event.target as HTMLInputElement ).checked )
        } )

    return chart
}

/**
 * Erzeugt die Umschaltung für die Angebots-Spur über dem Chart.
 *
 * Der Chart bekommt einen eigenen Bereich darunter, damit das Bedienelement
 * beim Neuzeichnen unangetastet bleibt.
 *
 * @param container Container aus dem Template.
 *
 * @returns Element, in das der Chart gerendert wird.
 */
function render_offer_toggle( container: HTMLElement ): HTMLElement {
    const controls = document.createElement( 'div' )
    controls.className = 'gt-deviation-chart__controls'

    const field = document.createElement( 'div' )
    field.className = 'ui toggle checkbox'

    const input = document.createElement( 'input' )
    input.type = 'checkbox'
    input.id = OFFER_TOGGLE_ID
    input.checked = true

    const label = document.createElement( 'label' )
    label.htmlFor = OFFER_TOGGLE_ID
    label.textContent = 'Angebot anzeigen'

    field.appendChild( input )
    field.appendChild( label )
    controls.appendChild( field )

    const canvas = document.createElement( 'div' )
    canvas.className = 'gt-deviation-chart__canvas'

    container.appendChild( controls )
    container.appendChild( canvas )

    return canvas
}
